import { useState } from 'react';
import { buildEvidenceLabel } from '../domain/sourceRef';
import { isBelowDeep, tierLabel } from '../domain/searchTier';
import { api } from '../api';

// EvidenceCallout — the always-on "what was cited" panel.
//
// Mounted alongside the document viewer whenever a citation is open.
// Shows the cited text verbatim with its source label and a match
// status indicator. Independent of whether the viewer succeeded in
// drawing a pixel-perfect highlight — the user always sees the
// evidence, even when the rendered DOM can't be wrapped (image-only
// PDFs, exotic text layers).
//
// Inputs are a small subset of the citation/SourceRef shape so the
// component can be reused by future producers (match-result evidence,
// contradiction view, timeline events) without coupling to citation
// internals.
export default function EvidenceCallout({
  snippet,
  filename,
  page,
  timestamp,
  verified,
  // 'located' | 'no-location' — overall match status. 'located' means
  // the viewer found and highlighted the cited text; 'no-location'
  // means we know what was cited but couldn't pinpoint it on the doc.
  locationStatus = 'no-location',
  // Search-readiness tier of the cited document + ids to run a deep-search
  // upgrade from here. When the doc is below deep, we tell the user more may
  // be available and offer a one-click upgrade.
  tier,
  documentId,
  caseId,
  onClose,
}) {
  const [collapsed, setCollapsed] = useState(false);
  // idle | upgrading | background | done | error — deep-search upgrade state.
  const [upgrade, setUpgrade] = useState('idle');
  const label = buildEvidenceLabel({ filename, page, timestamp });

  if (!snippet && !filename) return null;

  const belowDeep = isBelowDeep(tier) && tier !== 'excluded';
  const runDeepSearch = async () => {
    if (!caseId || !documentId) return;
    setUpgrade('upgrading');
    try {
      const res = await api.post(`/exploration/case/${caseId}/upgrade-tier`, {
        document_ids: [documentId],
        target_tier: 'deep',
        approved: true,
      });
      setUpgrade(res?.mode === 'background' && res.job_id ? 'background' : 'done');
    } catch {
      setUpgrade('error');
    }
  };

  return (
    <aside
      className={`evidence-callout ${collapsed ? 'evidence-callout--collapsed' : ''}`}
      role="complementary"
      aria-label="Cited evidence"
    >
      <header className="evidence-callout__header">
        <span className="evidence-callout__badge">
          {verified ? '✓ Verified citation' : '? Unverified'}
        </span>
        <button
          className="evidence-callout__toggle"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expand evidence panel' : 'Collapse evidence panel'}
        >
          {collapsed ? '▸' : '▾'}
        </button>
        {onClose && (
          <button
            className="evidence-callout__close"
            onClick={onClose}
            aria-label="Close evidence panel"
          >
            ✕
          </button>
        )}
      </header>
      {!collapsed && (
        <>
          <div className="evidence-callout__source">{label}</div>
          {snippet && (
            <blockquote className="evidence-callout__snippet">
              {snippet}
            </blockquote>
          )}
          <footer className="evidence-callout__status">
            {locationStatus === 'located' ? (
              <span className="evidence-callout__status--ok">
                ● Highlighted on document
              </span>
            ) : (
              <span className="evidence-callout__status--warn">
                ◆ Source confirmed; exact position not pinpointed on page
              </span>
            )}
          </footer>
          {belowDeep && (
            <div className="evidence-callout__tier">
              <span className="evidence-callout__tier-text">
                🔎 This document is <b>{tierLabel(tier).toLowerCase()}</b>-indexed —
                deep search may surface more.
              </span>
              {upgrade === 'idle' && caseId && documentId && (
                <button
                  className="evidence-callout__tier-btn"
                  onClick={runDeepSearch}
                  title="Fully index this document so citations and search cover every page"
                >
                  Run deep search
                </button>
              )}
              {upgrade === 'upgrading' && (
                <span className="evidence-callout__tier-note">Starting deep search…</span>
              )}
              {upgrade === 'background' && (
                <span className="evidence-callout__tier-note">Deep search running in the background…</span>
              )}
              {upgrade === 'done' && (
                <span className="evidence-callout__tier-note">✓ Deep search complete — reopen to see more.</span>
              )}
              {upgrade === 'error' && (
                <span className="evidence-callout__tier-note">Couldn’t start deep search — try again.</span>
              )}
            </div>
          )}
        </>
      )}
    </aside>
  );
}
